/**
 * Single row in the Recurring transactions desktop table.
 *
 * Renders one RecurringTemplate across whichever columns are
 * currently visible, in a CSS grid whose template is computed from
 * the same COLUMNS source as RecurringTableHead. Hidden columns are
 * removed from the grid entirely so the remaining columns re-flow.
 *
 * The three-dot button exposes its anchor via onOpenMenu(anchorEl)
 * so the parent can mount a Portal menu anchored to it.
 */

import { useRef, useState, type CSSProperties, type ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import type { RecurringTemplate } from "../recurringTypes";
import {
  COLUMNS,
  DEFAULT_VISIBLE_COLUMNS,
  computeGridTemplate,
  type ColumnKey,
} from "../recurringColumns";
import {
  formatAmount,
  formatDateLong,
  formatDateRelative,
  formatIntervalLabel,
  getPartyLabel,
  isDateSoon,
} from "../recurringUtils";
import { MODE_LABELS, TXN_TYPE_LABELS } from "../recurringMockData";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
  amber: "#B45309",
};

/** Days threshold for amber "coming up soon" indicator. */
const SOON_DAYS = 14;

interface RecurringRowProps {
  template: RecurringTemplate;
  onOpenMenu: (anchorEl: HTMLElement) => void;
  /** Set of visible column keys. Defaults to all defaults if omitted. */
  visibleColumns?: ReadonlySet<ColumnKey>;
}

export function RecurringRow({
  template,
  onOpenMenu,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
}: RecurringRowProps) {
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [hover, setHover] = useState(false);
  const paused = template.status === "paused";
  const ended = template.status === "ended";

  const handleMenuClick = () => {
    if (menuBtnRef.current) onOpenMenu(menuBtnRef.current);
  };

  const interval = formatIntervalLabel(template.schedule);
  const nextSoon = isDateSoon(template.nextDate, SOON_DAYS);
  const gridTemplate = computeGridTemplate(visibleColumns);

  const rowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: gridTemplate,
    columnGap: 14,
    alignItems: "center",
    padding: "13px 28px",
    borderBottom: `1px solid ${PALETTE.lineSoft}`,
    opacity: paused || ended ? 0.55 : 1,
    background: hover ? "#FAFBFC" : "#ffffff",
    transition: "background 0.1s, opacity 0.1s",
  };

  /** Content for a single column cell. Switch on the key keeps the row scanable. */
  const renderCell = (key: ColumnKey): ReactNode => {
    switch (key) {
      case "name":
        return (
          <div style={cellStyle}>
            <div style={nameStyle}>{template.name}</div>
          </div>
        );
      case "type":
        return (
          <div style={cellStyle}>
            <span style={modeChipStyle}>{MODE_LABELS[template.mode]}</span>
          </div>
        );
      case "txnType":
        return <div style={cellStyleMuted}>{TXN_TYPE_LABELS[template.txnType]}</div>;
      case "interval":
        return (
          <div style={cellStyleMuted}>
            <div>{interval.primary}</div>
            {interval.secondary && (
              <div style={subTextStyle}>{interval.secondary}</div>
            )}
          </div>
        );
      case "previous":
        return (
          <div style={cellStyleMuted}>
            <div>{formatDateLong(template.previousDate)}</div>
            {template.previousDate && (
              <div style={subTextStyle}>
                {formatDateRelative(template.previousDate)}
              </div>
            )}
          </div>
        );
      case "next":
        return (
          <div style={cellStyleMuted}>
            {paused || ended ? (
              <div style={{ color: PALETTE.ink4 }}>
                {ended ? "Ended" : "Paused"}
              </div>
            ) : (
              <>
                <div style={{ color: PALETTE.ink2, fontWeight: 500 }}>
                  {formatDateLong(template.nextDate)}
                </div>
                {template.nextDate && (
                  <div
                    style={{
                      ...subTextStyle,
                      color: nextSoon ? PALETTE.amber : PALETTE.ink4,
                      fontWeight: nextSoon ? 600 : 500,
                    }}
                  >
                    {formatDateRelative(template.nextDate)}
                  </div>
                )}
              </>
            )}
          </div>
        );
      case "party":
        return (
          <div style={cellStyleMuted}>
            {template.party.type === "internal" ? (
              <span style={{ fontStyle: "italic", color: PALETTE.ink4 }}>
                (internal)
              </span>
            ) : (
              getPartyLabel(template)
            )}
          </div>
        );
      case "amount":
        return (
          <div style={{ ...cellStyle, textAlign: "right" }}>
            <span style={amountStyle}>
              {formatAmount(template.transaction.amount)}
            </span>
          </div>
        );
      case "action":
        return (
          <div style={{ ...cellStyle, textAlign: "center" }}>
            <button
              ref={menuBtnRef}
              type="button"
              aria-label={`Actions for ${template.name}`}
              onClick={handleMenuClick}
              style={menuBtnStyle}
            >
              <MoreHorizontal size={16} />
            </button>
          </div>
        );
    }
  };

  return (
    <div
      role="row"
      style={rowStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {COLUMNS.filter((c) => visibleColumns.has(c.key)).map((col) => (
        <div key={col.key} style={{ display: "contents" }}>
          {renderCell(col.key)}
        </div>
      ))}
    </div>
  );
}

// ---------- Styles ----------

const cellStyle: CSSProperties = {
  fontSize: 13,
  color: PALETTE.ink,
  overflow: "hidden",
};

const cellStyleMuted: CSSProperties = {
  ...cellStyle,
  fontSize: 12.5,
  color: PALETTE.ink2,
};

const nameStyle: CSSProperties = {
  fontWeight: 600,
  color: PALETTE.ink,
  fontSize: 13,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const subTextStyle: CSSProperties = {
  fontSize: 10.5,
  color: PALETTE.ink4,
  marginTop: 2,
};

const modeChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  fontSize: 11.5,
  fontWeight: 500,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 4,
  color: PALETTE.ink2,
  background: "#ffffff",
};

const amountStyle: CSSProperties = {
  fontWeight: 600,
  fontFamily: "ui-monospace, Menlo, monospace",
  fontSize: 13,
  color: PALETTE.ink,
};

const menuBtnStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink3,
  padding: "4px 8px",
  cursor: "pointer",
  borderRadius: 6,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

export default RecurringRow;